<template>
  <div
    class="size-guide-tables"
    :class="{
      'size-guide-tables--drawer': drawer,
      'container': !drawer,
    }"
  >
    <div class="grid">
      <div
        class="col xs6"
        :class="{ 'l2-11' : !drawer }"
      >
        <div
          v-if="blocks.length > 1"
          class="size-guide-tables__tabs"
        >
          <btn
            v-for="(block, index) of blocks"
            :key="`tab-${block.title}-${index}`"
            class="size-guide-tables__tab"
            :label="block.title"
            :modifiers="
              activeBlock.handle === block.handle
                ? 'tab active'
                : 'tab'
            "
            :show-icon="false"
            type="button"
            @click.prevent="handleTabClick(block)"
          />
        </div>

        <div
          v-if="activeBlock.logo"
          class="size-guide-tables__logo-container"
        >
          <div class="size-guide-tables__logo">
            <responsive-image
              :alt="activeBlock.title"
              block-class="size-guide-tables"
              :image="{
                alt: activeBlock.title,
                src: activeBlock.logo
              }"
              :ratio="3"
              min-max="200-600"
              :sizes="logoSize"
            />
          </div>
        </div>

        <label
          class="size-guide-tables__unit-toggle unit-toggle"
          for="unit-toggle"
          aria-labelledby="toggle-label"
        >
          <span
            id="toggle-label"
            class="visually-hidden"
            v-text="toggleLabel"
          />

          <input
            id="unit-toggle"
            v-model="cm"
            class="unit-toggle__input"
            name="unit-toggle"
            type="checkbox"
          >

          <span
            class="unit-toggle__label text-heading-2xs"
            :class="{ [$class('active')]: !cm }"
            aria-hidden="true"
            v-text="$string('general.page.units.inch')"
          />

          <span
            class="unit-toggle__toggle"
            aria-hidden="true"
          />

          <span
            class="unit-toggle__label text-heading-2xs"
            :class="{ [$class('active')]: cm }"
            aria-hidden="true"
            v-text="$string('general.page.units.centimetre')"
          />
        </label>

        <p
          v-if="drawer"
          class="size-guide-tables__brand text-heading-s"
          v-text="activeBlock.title"
        />

        <div
          class="size-guide-tables__table rte"
          :class="{ 'size-guide-tables__table--bold-first-column': activeBlock.boldFirstColumn }"
          v-html="activeTable"
        />

        <p
          v-if="drawer && activeSubtext"
          class="size-guide-tables__subtext-title text-heading-s"
          v-text="$string('product.form.how_to_measure')"
        />

        <div
          v-if="activeSubtext"
          class="size-guide-tables__subtext"
          v-html="activeSubtext"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Size guide tables (size-guide-tables)
 * -----------------------------------------------------------------------------
 * Size guide tables with tab, unit toggle, and table.
 *
 * @param {Array} blocks - Formatted size guide blocks.
 * @param {String} defaultSubtext - Default subtext.
 * @param {String} defaultUnits - Default units, either `in` or `cm`.
 * @param {Boolean} drawer - Rendered inside drawer.
 *
 */
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'

import breakpoints from '~/config/breakpoints'

export default {
  name: 'SizeGuideTables',

  components: {
    Btn,
    ResponsiveImage,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    defaultSubtext: {
      type: String,
      default: '',
    },
    defaultUnits: {
      type: String,
      default: 'in',
    },
    drawer: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeBlock: {
        handle: 'loading',
      },
      cm: false,
    }
  },

  computed: {

    /**
     * Compute active subtext.
     * @returns {String}
     */
    activeSubtext() {
      const activeSubtext = this.activeBlock.subtext
        ? this.activeBlock.subtext
        : this.defaultSubtext

      return this.drawer
        ? activeSubtext
          .replaceAll('<p><strong', '<strong class="text-heading-2xs text-heading-xs-desktop"')
          .replaceAll('</strong></p>', '</strong>')
          .replaceAll('<p', '<p class="text-body-xs text-body-s-desktop"')
        : activeSubtext
          .replaceAll('<p', '<p class="text-body-2xs text-body-xs-desktop"')
    },

    /**
     * Compute active table with current units.
     * @returns {String}
     */
    activeTable() {
      if (!this.activeBlock.table) {
        return ''
      }

      const inch = this.$string('general.page.units.inch_short')
      const centimetre = this.$string('general.page.units.centimetre_short')

      let csv = this.activeBlock.table

      /**
       * Update heading units.
       */
      csv = this.cm
        ? csv.replaceAll('#unit#', centimetre)
        : csv.replaceAll('#unit#', inch)

      /**
       * Update displayed measurement based on unit.
       * - CM measurement comes second in cell.
       */
      csv = csv.replace(/(?<in>[\d.-]+)\|\|(?<cm>[\d.-]+)/g, (...args) => {
        const groups = args[args.length - 1]
        return this.cm ? groups.cm : groups.in
      })

      /**
       * Build table from CSV.
       */
      csv = csv.split('\n')

      const tableContent = csv.map((row, index) => {
        const rowContent = row.split(',').map((cell) => {
          const element = index === 0 ? 'th' : 'td'
          return `<${element} class="text-body-xs text-body-s">${cell}</${element}>`
        }).join('')

        return `<tr>${rowContent}</tr>`
      })

      const tHead = tableContent[0]
      const tBody = tableContent.slice(1).join('')

      return `
        <table>
          <thead>${tHead}
          <tbody>${tBody}</tbody>
        </table>
      `
    },

    /**
     * Compute logo size.
     * @returns {String}
     */
    logoSize() {
      return `(min-width: ${breakpoints.l}) 300px, 240px`
    },

    /**
     * Compute aria-label for toggle.
     * @returns {String}
     */
    toggleLabel() {
      const unit = this.cm
        ? this.$string('general.page.units.centimetre')
        : this.$string('general.page.units.inch')

      return this.$string('general.page.units.displaying', { replace: { unit } })
    },
  },

  mounted() {
    this.activeBlock = this.blocks[0]
    this.cm = this.defaultUnits === 'cm'
  },

  methods: {

    /**
     * Handle tab click.
     * @param {Object} block - Clicked block.
     */
    handleTabClick(block) {
      this.activeBlock = block
    },
  },
}
</script>

<style lang="scss">
@import './size-guide-tables';
</style>
